import React from 'react';
import { CheckCircle2, CreditCard, UserPlus, Unlock } from 'lucide-react';
import { readPurchaseIntent } from '@/lib/purchaseIntent';

/**
 * "YOU ARE HERE" STRIP for the sign-up / sign-in pages when the visitor
 * arrived by clicking Buy / Join. It tells a first-time customer, in three
 * words each, what is left — and that they will NOT have to find the lead or
 * plan again afterwards. Used by Register.tsx and Login.tsx.
 */
const PurchaseSteps: React.FC<{ buy: 'lead' | 'plan'; lang: string; signIn?: boolean }> = ({ buy, lang, signIn }) => {
  const es = lang === 'es';
  const intent = readPurchaseIntent();
  const label = intent?.label;

  const steps =
    buy === 'lead'
      ? [
          { icon: UserPlus, text: signIn ? (es ? 'Iniciar sesión' : 'Sign in') : es ? 'Crear cuenta' : 'Create account' },
          { icon: CreditCard, text: es ? 'Pagar el lead' : 'Pay for the lead' },
          { icon: Unlock, text: es ? 'Ver el contacto' : 'See the contact' },
        ]
      : [
          { icon: UserPlus, text: signIn ? (es ? 'Iniciar sesión' : 'Sign in') : es ? 'Crear cuenta' : 'Create account' },
          { icon: CreditCard, text: es ? 'Pagar la membresía' : 'Pay for membership' },
          { icon: CheckCircle2, text: es ? 'Recibir créditos' : 'Get your credits' },
        ];

  return (
    <div className="mb-6 rounded-2xl border-2 border-orange-200 bg-orange-50 p-4 sm:p-5">
      <p className="text-sm font-extrabold text-orange-900">
        {buy === 'lead'
          ? es
            ? 'Está a un paso de comprar este lead'
            : 'You are one step away from buying this lead'
          : es
          ? 'Está a un paso de su membresía'
          : 'You are one step away from your membership'}
        {label ? <span className="font-bold text-orange-700"> — {label}</span> : null}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-orange-800">
        {es
          ? 'Cuando termine aquí, lo llevamos directo al pago. No tiene que buscar nada.'
          : 'As soon as you finish here we take you straight to payment. Nothing to search for.'}
      </p>
      <ol className="mt-3 grid grid-cols-3 gap-2">
        {steps.map((s, i) => (
          <li
            key={s.text}
            className={`flex flex-col items-center gap-1 rounded-xl px-2 py-2 text-center text-[11px] font-bold sm:text-xs ${
              i === 0 ? 'bg-orange-500 text-white' : 'bg-white text-slate-500'
            }`}
          >
            <s.icon className="h-4 w-4" />
            <span>
              {i + 1}. {s.text}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default PurchaseSteps;
